/**
 * useInfiniteScroll - Load more items as user scrolls
 * Mobile-friendly replacement for pagination
 */

import { useEffect, useRef, useCallback } from 'react';

export interface UseInfiniteScrollOptions {
  /** Callback to load more items */
  onLoadMore: () => void;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether currently loading */
  isLoading: boolean;
  /** Distance from bottom (in px) to trigger load */
  threshold?: number;
  /** Scroll container ref (defaults to window) */
  scrollRef?: React.RefObject<HTMLElement>;
}

export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 200,
  scrollRef,
}: UseInfiniteScrollOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Sentinel element ref - place this at the bottom of your list
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;

      // Disconnect previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node || !hasMore) return;

      // Create new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting && hasMore && !isLoading) {
            onLoadMore();
          }
        },
        {
          root: scrollRef?.current || null,
          rootMargin: `${threshold}px`,
          threshold: 0,
        }
      );

      observerRef.current.observe(node);
    },
    [hasMore, isLoading, onLoadMore, threshold, scrollRef]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { sentinelRef };
}
